/**
 * ManualOverlay — the in-game playbook / help / wiki.
 *
 * A full-screen slide-up sheet with a tab strip over five sections (Lobby,
 * Craps, Cee-lo, Wallet, My Dice). Opened from the home hub help button and
 * from the in-game menu in both cee-lo and craps; the in-game entry can open
 * straight to the section for the game you're in.
 *
 * Copy lives in manualContent.ts (localization ready). This file owns ONLY
 * presentation: icons, accent colors, dice-face graphics, and the renderer
 * that turns content blocks into DOM.
 *
 * Conventions honored:
 *  - Full-screen bottom-pinned scroll sheet (PlayersGridOverlay shape).
 *  - Tab strip / panel switch (CrewScreen shape).
 *  - Flicker-free entrance: force a reflow before adding `.visible` so the
 *    slide-up transition runs from the off-screen state (CLAUDE.md rule). The
 *    sheet is also pinned to its own GPU layer (will-change) so Android doesn't
 *    re-rasterize and flash it for a frame when the slide-in transition ends.
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { sound, SoundId } from '../../../engine/audio/sound';
import { getSidebetIconUrl } from '../sidebetIcon';
import {
  MANUAL_SECTIONS,
  type ManualBlock,
  type ManualSection,
  type ManualSectionId,
  type ManualTone,
} from './manualContent';

const STYLE_ID = 'manual-overlay-styles';

/** Tone → hex, for inline accent coloring. */
const TONE_HEX: Record<ManualTone, string> = {
  gold: '#FFD700',
  cyan: '#00F5FF',
  magenta: '#FF0080',
  green: '#00FF88',
  red: '#FF3366',
  purple: '#A56BFF',
};

/**
 * Tone → "r,g,b" channels. We avoid color-mix() (unsupported on older Android
 * WebViews, and this audience runs old phones) and instead build translucent
 * accent tints with rgba(var(--accent-rgb), a), which works everywhere.
 */
const TONE_RGB: Record<ManualTone, string> = {
  gold: '255,215,0',
  cyan: '0,245,255',
  magenta: '255,0,128',
  green: '0,255,136',
  red: '255,51,102',
  purple: '165,107,255',
};

/** Which of the 9 cells (row-major 3x3) carry a pip for each die value. */
const PIP_MAP: Record<number, number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

const CLOSE_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>`;

/** Per-section tab icon (presentation only, keyed by section id). */
const SECTION_ICON: Record<ManualSectionId, string> = {
  lobby: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`,
  craps: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="4"/><circle cx="8" cy="8" r="1.4" fill="currentColor" stroke="none"/><circle cx="16" cy="16" r="1.4" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/></svg>`,
  ceelo: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="4"/><circle cx="8" cy="8" r="1.4" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/><circle cx="16" cy="16" r="1.4" fill="currentColor" stroke="none"/></svg>`,
  wallet: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7a2 2 0 0 1 2-2h12v3"/><rect x="3" y="7" width="18" height="12" rx="2"/><circle cx="16" cy="13" r="1.6" fill="currentColor" stroke="none"/></svg>`,
  dice: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><rect x="4" y="4" width="16" height="16" rx="4"/><circle cx="9" cy="9" r="1.3" fill="currentColor" stroke="none"/><circle cx="15" cy="9" r="1.3" fill="currentColor" stroke="none"/><circle cx="9" cy="15" r="1.3" fill="currentColor" stroke="none"/><circle cx="15" cy="15" r="1.3" fill="currentColor" stroke="none"/></svg>`,
};

interface ManualOverlayEvents {
  open: void;
  close: void;
}

export class ManualOverlay extends EventEmitter<ManualOverlayEvents> {
  public element: HTMLElement;
  private tabStrip: HTMLElement;
  private bodyEl: HTMLElement;
  private open_ = false;

  constructor(container: HTMLElement = document.body) {
    super();
    this.injectStyles();
    this.element = this.createElement();
    this.tabStrip = this.element.querySelector('.manual-tabs')!;
    this.bodyEl = this.element.querySelector('.manual-body')!;
    container.appendChild(this.element);
    this.wireChrome();
    this.buildTabs();
  }

  // ─── Public API ───────────────────────────────────────────────────

  /** Open the manual, optionally jumping straight to a section. */
  open(sectionId: ManualSectionId = 'lobby'): void {
    this.selectSection(sectionId, false);
    if (this.open_) return;
    this.open_ = true;
    this.element.style.display = 'flex';
    this.element.style.pointerEvents = '';
    this.bodyEl.scrollTop = 0;
    // Commit the off-screen start state before flipping `.visible` so the
    // slide-up transition runs from the hidden frame (no Android flash).
    void this.element.offsetHeight;
    this.element.classList.add('visible');
    sound.play(SoundId.SHEET_OPEN);
    this.emit('open', undefined);
  }

  close(): void {
    if (!this.open_) return;
    this.open_ = false;
    this.element.classList.remove('visible');
    // Drop pointer capture during the slide-out so the backdrop can't eat taps
    // meant for whatever is underneath (matches InGameMenu.close).
    this.element.style.pointerEvents = 'none';
    sound.play(SoundId.SHEET_CLOSE);
    setTimeout(() => {
      if (!this.open_) this.element.style.display = 'none';
    }, 360);
    this.emit('close', undefined);
  }

  isOpen(): boolean {
    return this.open_;
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  // ─── DOM ──────────────────────────────────────────────────────────

  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'manual-backdrop';
    el.style.display = 'none';
    el.innerHTML = `
      <div class="manual-sheet" role="dialog" aria-label="Game manual">
        <div class="manual-grip"></div>
        <div class="manual-header">
          <span class="manual-kicker">The Playbook</span>
          <button class="manual-close" type="button" aria-label="Close">${CLOSE_ICON}</button>
        </div>
        <div class="manual-tabs" role="tablist"></div>
        <div class="manual-body"></div>
      </div>
    `;
    return el;
  }

  private wireChrome(): void {
    // Tap the dimmed area above the sheet to close.
    this.element.addEventListener('pointerdown', (e) => {
      if (e.target === this.element) this.close();
    });
    this.element.querySelector('.manual-close')!.addEventListener('click', () => this.close());
    this.element.querySelector('.manual-grip')!.addEventListener('click', () => this.close());
  }

  private buildTabs(): void {
    this.tabStrip.innerHTML = '';
    for (const section of MANUAL_SECTIONS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'manual-tab';
      btn.dataset.id = section.id;
      btn.style.setProperty('--accent', TONE_HEX[section.accent]);
      btn.style.setProperty('--accent-rgb', TONE_RGB[section.accent]);
      btn.innerHTML = `<span class="manual-tab-icon">${SECTION_ICON[section.id]}</span><span class="manual-tab-label">${section.tab}</span>`;
      btn.addEventListener('click', () => {
        sound.play(SoundId.UI_TAP);
        this.selectSection(section.id, true);
      });
      this.tabStrip.appendChild(btn);
    }
  }

  // ─── Section rendering ────────────────────────────────────────────

  private selectSection(id: ManualSectionId, scrollTop: boolean): void {
    const section = MANUAL_SECTIONS.find((s) => s.id === id) ?? MANUAL_SECTIONS[0];

    this.tabStrip.querySelectorAll<HTMLElement>('.manual-tab').forEach((t) => {
      t.classList.toggle('active', t.dataset.id === id);
    });

    this.renderSection(section);
    if (scrollTop) this.bodyEl.scrollTop = 0;

    // Bring the active tab fully into view on narrow screens.
    const activeTab = this.tabStrip.querySelector<HTMLElement>('.manual-tab.active');
    activeTab?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  private renderSection(section: ManualSection): void {
    const accent = TONE_HEX[section.accent];
    this.bodyEl.style.setProperty('--accent', accent);
    this.bodyEl.style.setProperty('--accent-rgb', TONE_RGB[section.accent]);
    this.bodyEl.innerHTML = '';

    const head = document.createElement('div');
    head.className = 'manual-section-head';
    head.innerHTML = `
      <h2 class="manual-section-title">${section.title}</h2>
      <p class="manual-section-tagline">${section.tagline}</p>
      <div class="manual-section-rule"></div>
    `;
    this.bodyEl.appendChild(head);

    for (const block of section.blocks) {
      this.bodyEl.appendChild(this.renderBlock(block, accent));
    }

    const foot = document.createElement('div');
    foot.className = 'manual-foot';
    this.bodyEl.appendChild(foot);
  }

  private renderBlock(block: ManualBlock, accent: string): HTMLElement {
    switch (block.type) {
      case 'lead': {
        const p = document.createElement('p');
        p.className = 'manual-lead';
        p.textContent = block.text;
        return p;
      }
      case 'p': {
        const p = document.createElement('p');
        p.className = 'manual-p';
        p.textContent = block.text;
        return p;
      }
      case 'heading': {
        const h = document.createElement('h3');
        h.className = 'manual-h';
        h.textContent = block.text;
        return h;
      }
      case 'tip': {
        const d = document.createElement('div');
        d.className = 'manual-tip';
        d.innerHTML = `<span class="manual-tip-tag">Tip</span><span class="manual-tip-text"></span>`;
        d.querySelector('.manual-tip-text')!.textContent = block.text;
        return d;
      }
      case 'dice': {
        return this.renderDiceRow(block.faces, block.caption);
      }
      case 'callout': {
        const hex = TONE_HEX[block.tone];
        const d = document.createElement('div');
        d.className = 'manual-callout';
        d.style.setProperty('--c', hex);
        d.style.setProperty('--c-rgb', TONE_RGB[block.tone]);
        if (block.title) {
          const t = document.createElement('div');
          t.className = 'manual-callout-title';
          t.textContent = block.title;
          d.appendChild(t);
        }
        const body = document.createElement('div');
        body.className = 'manual-callout-text';
        body.textContent = block.text;
        d.appendChild(body);
        return d;
      }
      case 'list': {
        const ul = document.createElement('ul');
        ul.className = 'manual-list';
        for (const item of block.items) {
          const li = document.createElement('li');
          li.textContent = item;
          ul.appendChild(li);
        }
        return ul;
      }
      case 'steps': {
        const ol = document.createElement('ol');
        ol.className = 'manual-steps';
        block.items.forEach((item, i) => {
          const li = document.createElement('li');
          li.className = 'manual-step';
          const num = document.createElement('span');
          num.className = 'manual-step-num';
          num.textContent = String(i + 1);
          const text = document.createElement('span');
          text.className = 'manual-step-text';
          const title = document.createElement('strong');
          title.textContent = item.title;
          text.appendChild(title);
          text.appendChild(document.createTextNode('. ' + item.text));
          li.appendChild(num);
          li.appendChild(text);
          ol.appendChild(li);
        });
        return ol;
      }
      case 'ranks': {
        const wrap = document.createElement('div');
        wrap.className = 'manual-ranks';
        for (const row of block.rows) {
          const hex = row.tone ? TONE_HEX[row.tone] : accent;
          const r = document.createElement('div');
          r.className = 'manual-rank';
          r.style.setProperty('--c', hex);
          const dice = document.createElement('div');
          dice.className = 'manual-rank-dice';
          if (row.faces) {
            for (const f of row.faces) dice.appendChild(this.renderDie(f));
          }
          const txt = document.createElement('div');
          txt.className = 'manual-rank-text';
          const lbl = document.createElement('div');
          lbl.className = 'manual-rank-label';
          lbl.textContent = row.label;
          const note = document.createElement('div');
          note.className = 'manual-rank-note';
          note.textContent = row.note;
          txt.appendChild(lbl);
          txt.appendChild(note);
          r.appendChild(dice);
          r.appendChild(txt);
          wrap.appendChild(r);
        }
        return wrap;
      }
      case 'bets': {
        const table = document.createElement('div');
        table.className = 'manual-bets';
        for (const row of block.rows) {
          const r = document.createElement('div');
          r.className = 'manual-bet';

          const top = document.createElement('div');
          top.className = 'manual-bet-top';

          // The real in-game chip (shared sidebet icon registry), so the
          // colors/glyphs here match exactly what the player taps at the table.
          const iconUrl = row.betType ? getSidebetIconUrl(row.betType, 'large') : '';
          if (iconUrl) {
            const icon = document.createElement('img');
            icon.className = 'manual-bet-icon';
            icon.src = iconUrl;
            icon.alt = '';
            top.appendChild(icon);
          }

          const name = document.createElement('div');
          name.className = 'manual-bet-name';
          name.textContent = row.name;
          top.appendChild(name);

          if (row.pays) {
            const pays = document.createElement('div');
            pays.className = 'manual-bet-pays';
            pays.textContent = row.pays;
            top.appendChild(pays);
          }

          r.appendChild(top);

          if (row.win || row.lose) {
            // Explicit, asymmetric win/lose (craps place bets). Green win,
            // red lose mirrors the table's outcome color language.
            if (row.win) r.appendChild(this.betOutcomeLine('Win', row.win, '#00FF88'));
            if (row.lose) r.appendChild(this.betOutcomeLine('Lose', row.lose, '#FF3366'));
          } else if (row.on) {
            const on = document.createElement('div');
            on.className = 'manual-bet-on';
            on.textContent = row.on;
            r.appendChild(on);
          }

          table.appendChild(r);
        }
        return table;
      }
    }
  }

  /** One "Win"/"Lose" line under a bet, color-tagged like the table outcomes. */
  private betOutcomeLine(tag: string, text: string, color: string): HTMLElement {
    const line = document.createElement('div');
    line.className = 'manual-bet-outcome';
    line.style.setProperty('--oc', color);
    const t = document.createElement('span');
    t.className = 'manual-bet-outcome-tag';
    t.textContent = tag;
    const v = document.createElement('span');
    v.className = 'manual-bet-outcome-text';
    v.textContent = text;
    line.appendChild(t);
    line.appendChild(v);
    return line;
  }

  private renderDiceRow(faces: number[], caption?: string): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'manual-dice-row';
    const dice = document.createElement('div');
    dice.className = 'manual-dice-set';
    for (const f of faces) dice.appendChild(this.renderDie(f));
    wrap.appendChild(dice);
    if (caption) {
      const cap = document.createElement('div');
      cap.className = 'manual-dice-caption';
      cap.textContent = caption;
      wrap.appendChild(cap);
    }
    return wrap;
  }

  /** A single CSS-drawn die face (3x3 pip grid). */
  private renderDie(value: number): HTMLElement {
    const die = document.createElement('div');
    die.className = 'manual-die';
    const pips = PIP_MAP[value] ?? [];
    for (let i = 0; i < 9; i++) {
      const cell = document.createElement('span');
      cell.className = 'manual-die-cell';
      if (pips.includes(i)) cell.classList.add('on');
      die.appendChild(cell);
    }
    return die;
  }

  // ─── Styles ───────────────────────────────────────────────────────

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .manual-backdrop {
        position: fixed;
        inset: 0;
        z-index: 1200;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        background: rgba(0, 0, 0, 0);
        transition: background 0.36s cubic-bezier(0.4, 0, 0.2, 1);
        pointer-events: auto;
      }
      .manual-backdrop.visible { background: rgba(0, 0, 0, 0.62); }

      .manual-sheet {
        width: 100%;
        max-width: 520px;
        height: 92vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background: linear-gradient(180deg, #15151B 0%, #0B0B0E 100%);
        border: 1px solid rgba(255, 255, 255, 0.07);
        border-bottom: none;
        border-radius: 22px 22px 0 0;
        box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.7);
        transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        /* Keep the sheet on its own GPU layer for good. Without this, Android
         * WebView collapses the composite layer it makes for the slide-up the
         * instant the transition ends, then re-rasterizes the heavy content on
         * the main layer — a one-frame flicker AFTER the animation completes.
         * will-change + a 3D transform pin the layer so there's no re-raster. */
        will-change: transform;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        transform: translateY(100%) translateZ(0);
      }
      .manual-backdrop.visible .manual-sheet { transform: translateY(0) translateZ(0); }

      .manual-grip {
        width: 44px; height: 4px;
        border-radius: 2px;
        background: rgba(255, 255, 255, 0.22);
        margin: 10px auto 6px;
        flex-shrink: 0;
        cursor: pointer;
      }

      .manual-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 4px 18px 10px;
        flex-shrink: 0;
      }
      .manual-kicker {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 26px;
        letter-spacing: 2px;
        color: #fff;
        text-shadow: 0 0 18px rgba(255, 215, 0, 0.25);
      }
      .manual-close {
        width: 34px; height: 34px;
        flex-shrink: 0;
        display: grid; place-items: center;
        padding: 0;
        border: none;
        border-radius: 10px;
        background: rgba(255, 255, 255, 0.06);
        color: rgba(255, 255, 255, 0.6);
        cursor: pointer;
      }
      .manual-close:hover { background: rgba(255, 51, 102, 0.16); color: #FF3366; }
      .manual-close svg { width: 16px; height: 16px; }

      /* ── Tab strip ── */
      .manual-tabs {
        display: flex;
        gap: 6px;
        padding: 0 14px 10px;
        overflow-x: auto;
        flex-shrink: 0;
        scrollbar-width: none;
        border-bottom: 1px solid rgba(255, 255, 255, 0.07);
      }
      .manual-tabs::-webkit-scrollbar { height: 0; }
      .manual-tab {
        flex: 1 0 auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        min-width: 62px;
        padding: 9px 8px 8px;
        border: 1px solid rgba(255, 255, 255, 0.06);
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.025);
        color: rgba(255, 255, 255, 0.5);
        cursor: pointer;
        transition: color 0.15s, border-color 0.15s, background 0.15s;
        -webkit-tap-highlight-color: transparent;
      }
      .manual-tab-icon { display: grid; place-items: center; }
      .manual-tab-icon svg { width: 22px; height: 22px; }
      .manual-tab-label {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 700;
        font-size: 12px;
        letter-spacing: 0.4px;
        white-space: nowrap;
      }
      .manual-tab:active { transform: scale(0.96); }
      .manual-tab.active {
        color: var(--accent);
        border-color: rgba(var(--accent-rgb), 0.55);
        background: rgba(var(--accent-rgb), 0.12);
        box-shadow: 0 0 16px -4px var(--accent);
      }

      /* ── Body ── */
      .manual-body {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        -webkit-overflow-scrolling: touch;
        padding: 16px 18px calc(env(safe-area-inset-bottom, 0px) + 20px);
      }
      .manual-body::-webkit-scrollbar { width: 0; }

      .manual-section-head { margin-bottom: 14px; }
      .manual-section-title {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 38px;
        letter-spacing: 1.5px;
        line-height: 1;
        margin: 0;
        color: #fff;
      }
      .manual-section-tagline {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 600;
        font-size: 14px;
        color: var(--accent);
        margin: 6px 0 0;
      }
      .manual-section-rule {
        height: 3px;
        width: 56px;
        margin-top: 12px;
        border-radius: 2px;
        background: var(--accent);
        box-shadow: 0 0 14px -2px var(--accent);
      }

      .manual-lead {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 600;
        font-size: 17px;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.92);
        margin: 0 0 16px;
      }
      .manual-p {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 500;
        font-size: 15px;
        line-height: 1.55;
        color: rgba(255, 255, 255, 0.72);
        margin: 0 0 14px;
      }
      .manual-h {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 22px;
        letter-spacing: 1px;
        color: #fff;
        margin: 22px 0 12px;
        padding-left: 12px;
        border-left: 3px solid var(--accent);
      }

      /* ── Callout ── */
      .manual-callout {
        position: relative;
        padding: 13px 15px 14px;
        margin: 0 0 14px;
        border-radius: 13px;
        background: rgba(var(--c-rgb), 0.09);
        border: 1px solid rgba(var(--c-rgb), 0.35);
        border-left: 3px solid var(--c);
      }
      .manual-callout-title {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 19px;
        letter-spacing: 0.8px;
        color: var(--c);
        margin-bottom: 4px;
      }
      .manual-callout-text {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 500;
        font-size: 14.5px;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.82);
      }

      /* ── List ── */
      .manual-list {
        list-style: none;
        margin: 0 0 14px;
        padding: 0;
      }
      .manual-list li {
        position: relative;
        padding: 6px 0 6px 20px;
        font-family: 'Rajdhani', sans-serif;
        font-weight: 500;
        font-size: 15px;
        line-height: 1.45;
        color: rgba(255, 255, 255, 0.78);
      }
      .manual-list li::before {
        content: '';
        position: absolute;
        left: 2px;
        top: 14px;
        width: 7px; height: 7px;
        border-radius: 50%;
        background: var(--accent);
        box-shadow: 0 0 8px -1px var(--accent);
      }

      /* ── Steps ── */
      .manual-steps { list-style: none; counter-reset: step; margin: 0 0 14px; padding: 0; }
      .manual-step {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 8px 0;
      }
      .manual-step-num {
        flex-shrink: 0;
        width: 28px; height: 28px;
        display: grid; place-items: center;
        border-radius: 9px;
        background: rgba(var(--accent-rgb), 0.16);
        border: 1px solid rgba(var(--accent-rgb), 0.45);
        color: var(--accent);
        font-family: 'Bebas Neue', sans-serif;
        font-size: 17px;
      }
      .manual-step-text {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 500;
        font-size: 15px;
        line-height: 1.45;
        color: rgba(255, 255, 255, 0.75);
        padding-top: 3px;
      }
      .manual-step-text strong { color: #fff; font-weight: 700; }

      /* ── Tip ── */
      .manual-tip {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 11px 14px;
        margin: 4px 0 16px;
        border-radius: 12px;
        background: rgba(255, 215, 0, 0.07);
        border: 1px dashed rgba(255, 215, 0, 0.35);
      }
      .manual-tip-tag {
        flex-shrink: 0;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 14px;
        letter-spacing: 1px;
        color: #FFD700;
        padding: 2px 8px;
        border-radius: 6px;
        background: rgba(255, 215, 0, 0.14);
      }
      .manual-tip-text {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 600;
        font-size: 14px;
        line-height: 1.4;
        color: rgba(255, 255, 255, 0.85);
      }

      /* ── Dice graphics ── */
      .manual-die {
        width: 38px; height: 38px;
        flex-shrink: 0;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(3, 1fr);
        gap: 1px;
        padding: 5px;
        border-radius: 9px;
        background: linear-gradient(145deg, #fdfdfd, #d6d6dc);
        box-shadow: inset 0 1px 2px rgba(255,255,255,0.9), 0 3px 8px rgba(0,0,0,0.45);
      }
      .manual-die-cell { display: grid; place-items: center; }
      .manual-die-cell.on::after {
        content: '';
        width: 6px; height: 6px;
        border-radius: 50%;
        background: radial-gradient(circle at 35% 30%, #4a4a52, #111114);
      }

      .manual-dice-row {
        display: flex;
        align-items: center;
        gap: 14px;
        margin: 4px 0 16px;
      }
      .manual-dice-set { display: flex; gap: 7px; }
      .manual-dice-caption {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 600;
        font-size: 14px;
        color: rgba(255, 255, 255, 0.6);
      }

      /* ── Ranks ── */
      .manual-ranks { display: flex; flex-direction: column; gap: 8px; margin: 0 0 16px; }
      .manual-rank {
        display: flex;
        align-items: center;
        gap: 13px;
        padding: 11px 13px;
        border-radius: 13px;
        background: rgba(255, 255, 255, 0.025);
        border: 1px solid rgba(255, 255, 255, 0.06);
        border-left: 3px solid var(--c);
      }
      .manual-rank-dice { display: flex; gap: 5px; flex-shrink: 0; }
      .manual-rank-dice .manual-die { width: 30px; height: 30px; padding: 4px; border-radius: 7px; }
      .manual-rank-dice .manual-die-cell.on::after { width: 4.5px; height: 4.5px; }
      .manual-rank-text { min-width: 0; }
      .manual-rank-label {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 19px;
        letter-spacing: 0.6px;
        color: var(--c);
        line-height: 1.1;
      }
      .manual-rank-note {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 500;
        font-size: 13px;
        line-height: 1.35;
        color: rgba(255, 255, 255, 0.66);
        margin-top: 2px;
      }

      /* ── Bets table ── */
      .manual-bets {
        display: flex;
        flex-direction: column;
        gap: 1px;
        margin: 0 0 16px;
        border-radius: 13px;
        overflow: hidden;
        border: 1px solid rgba(255, 255, 255, 0.07);
      }
      .manual-bet {
        padding: 11px 14px;
        background: rgba(255, 255, 255, 0.022);
      }
      .manual-bet-top {
        display: flex;
        align-items: center;
        gap: 11px;
      }
      .manual-bet-icon {
        width: 30px;
        height: 30px;
        flex-shrink: 0;
        display: block;
        filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.5));
      }
      .manual-bet-name {
        flex: 1;
        min-width: 0;
        font-family: 'Rajdhani', sans-serif;
        font-weight: 700;
        font-size: 15px;
        color: #fff;
      }
      .manual-bet-pays {
        flex-shrink: 0;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 17px;
        letter-spacing: 0.5px;
        color: var(--accent);
        white-space: nowrap;
      }
      .manual-bet-on {
        margin-top: 5px;
        font-family: 'Rajdhani', sans-serif;
        font-weight: 500;
        font-size: 13px;
        line-height: 1.35;
        color: rgba(255, 255, 255, 0.58);
      }
      .manual-bet-outcome {
        display: flex;
        align-items: baseline;
        gap: 8px;
        margin-top: 5px;
      }
      .manual-bet-outcome-tag {
        flex-shrink: 0;
        width: 38px;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 13px;
        letter-spacing: 0.6px;
        color: var(--oc);
      }
      .manual-bet-outcome-text {
        font-family: 'Rajdhani', sans-serif;
        font-weight: 500;
        font-size: 13px;
        line-height: 1.35;
        color: rgba(255, 255, 255, 0.66);
      }

      .manual-foot { height: 8px; }
    `;
    document.head.appendChild(style);
  }
}
